/**
 * storageHelper.js
 * Centralized Storage Manager with 24-hour TTL for Remember Me functionality
 */

export const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

/**
 * Save data to localStorage with expiration timestamp (TTL)
 */
export function setLocalToken(key, value, ttlMs = TWENTY_FOUR_HOURS_MS) {
  try {
    const payload = {
      value,
      expiry: Date.now() + ttlMs,
      savedAt: Date.now(),
    };
    localStorage.setItem(key, JSON.stringify(payload));
  } catch (e) {
    console.error('Failed to set localStorage with TTL:', e);
  }
}

/**
 * Read data from localStorage, automatically purging expired items
 */
export function getLocalToken(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw || raw === 'undefined' || raw === 'null') return null;

    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && 'expiry' in parsed) {
      if (Date.now() > parsed.expiry) {
        localStorage.removeItem(key);
        return null;
      }
      return parsed.value;
    }

    // Backward compatibility if raw data was stored without envelope
    return parsed;
  } catch (e) {
    const raw = localStorage.getItem(key);
    return raw || null;
  }
}

/**
 * Remove an item from localStorage
 */
export function removeLocalToken(key) {
  try {
    localStorage.removeItem(key);
  } catch (err) {
    console.warn('removeLocalToken error:', err);
  }
}

/**
 * Get item checking sessionStorage first, then localStorage with TTL check
 */
export function getAuthStorageItem(key) {
  try {
    // 1. Check sessionStorage
    const sessionVal = sessionStorage.getItem(key);
    if (sessionVal && sessionVal !== 'undefined' && sessionVal !== 'null') {
      try {
        return JSON.parse(sessionVal);
      } catch (err) {
        return sessionVal;
      }
    }

    // 2. Check localStorage with TTL check
    const localVal = getLocalToken(key);
    if (localVal !== null && localVal !== undefined) {
      return localVal;
    }
  } catch (err) {
    console.warn('getAuthStorageItem error:', err);
  }

  return null;
}

/**
 * Broadcast an authentication event to all other tabs via both BroadcastChannel and localStorage
 */
export function broadcastAuthChange(action = 'logout') {
  try {
    const payload = JSON.stringify({ action, timestamp: Date.now() });
    localStorage.setItem('meya_auth_broadcast', payload);
    if (typeof BroadcastChannel !== 'undefined') {
      const bc = new BroadcastChannel('meya_job_auth_channel');
      bc.postMessage({ action, timestamp: Date.now() });
      bc.close();
    }
  } catch (err) {
    // Storage or BroadcastChannel unavailable
  }
}

/**
 * Clear all authentication tokens and session indicators across both storages
 */
export function clearAllAuthTokens(broadcast = false) {
  try {
    // Clear Meya shop tokens
    sessionStorage.removeItem('meya-token4');
    sessionStorage.removeItem('meya-rem');
    localStorage.removeItem('meya-token4');
    localStorage.removeItem('meya-rem');

    // Clear Job seeker tokens and status
    sessionStorage.removeItem('meya-job-loggedin');
    sessionStorage.removeItem('meya-job-username');
    sessionStorage.removeItem('job_token');
    sessionStorage.removeItem('job_user_id');

    localStorage.removeItem('meya-job-loggedin');
    localStorage.removeItem('meya-job-username');
    localStorage.removeItem('job_token');
    localStorage.removeItem('job_user_id');
    localStorage.removeItem('job_user_email');
    localStorage.removeItem('job_user_phone');
    localStorage.removeItem('job_remember_me');

    if (broadcast) {
      broadcastAuthChange('logout');
    }
  } catch (err) {
    console.warn('clearAllAuthTokens error:', err);
  }
}
